import { useState, useEffect, useCallback } from 'react';
import { UserEAConfig, UserEAConfigService } from '../services/UserEAConfigService';
import { logger } from 'src/logger';

interface UseUserEAConfigProps {
  userAlias: string;
}

export const useUserEAConfig = ({ userAlias }: UseUserEAConfigProps) => {
  const [configs, setConfigs] = useState<UserEAConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadConfigs = useCallback(async () => {
    if (!userAlias) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await UserEAConfigService.getAll(userAlias, true);
      setConfigs(data);
    } catch (err) {
      logger.warn('Failed to load user EA configs', { error: err, userAlias });
    } finally {
      setIsLoading(false);
    }
  }, [userAlias]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const saveConfig = useCallback(
    async (config: UserEAConfig) => {
      await UserEAConfigService.save(userAlias, config);
      await loadConfigs();
    },
    [userAlias, loadConfigs]
  );

  const deleteConfig = useCallback(
    async (leaderAlias: string) => {
      await UserEAConfigService.delete(userAlias, leaderAlias);
      await loadConfigs();
    },
    [userAlias, loadConfigs]
  );

  return {
    configs,
    isLoading,
    saveConfig,
    deleteConfig,
    refreshConfigs: loadConfigs
  };
};
